/**
 * Places a hovered vendor label that is wider than its own grid column.
 *
 * A packed vendor header spans the columns its cards occupy, so the span comes
 * from the card count and knows nothing about how wide the label is. A
 * multi-organization attribution therefore routinely needs more room than its
 * span provides. The stylesheet lays such a label out at its natural width and
 * fades the overflow at the column edge; hovering lifts the fade and floats the
 * whole label over its neighbours. This decides where that floated label sits.
 */

export interface VendorLabelBox {
  /** Viewport x of the label's left edge. */
  labelLeft: number;
  /** Natural width of the label, including whatever overflows the column. */
  labelWidth: number;
  /** Viewport x of the right edge of the label's own grid column. */
  columnRight: number;
  /** Viewport x of the header row's left edge. */
  rowLeft: number;
  /** Viewport x of the header row's right edge. */
  rowRight: number;
}

export interface VendorLabelExpansion {
  /** True when the label overflows its column, and so is faded when at rest. */
  expanded: boolean;
  /** Pixels to translate the expanded label by. Never positive. */
  shift: number;
}

/** Sub-pixel slack, so a label that merely rounds past its column stays put. */
const TOLERANCE = 0.5;

/**
 * True when content ending at `contentRight` runs past the column ending at
 * `columnRight`. Only such a label is faded at rest and expanded on hover.
 */
export function vendorLabelOverflows(
  contentRight: number,
  columnRight: number
): boolean {
  return contentRight > columnRight + TOLERANCE;
}

/**
 * A label that fits its column is never expanded: nothing is faded, so there is
 * nothing to reveal. One that overflows grows rightward while the row has room,
 * and otherwise pins its right edge to the row and grows leftward instead.
 */
export function planVendorLabelExpansion(
  box: VendorLabelBox
): VendorLabelExpansion {
  const { labelLeft, labelWidth, columnRight, rowLeft, rowRight } = box;
  const labelRight = labelLeft + labelWidth;

  if (!vendorLabelOverflows(labelRight, columnRight)) {
    return { expanded: false, shift: 0 };
  }

  const overshoot = labelRight - rowRight;
  const shift = overshoot > TOLERANCE ? -overshoot : 0;

  // A label wider than the whole row cannot fit in either direction. Stop at
  // the row's left edge rather than paint outside the scroll container.
  const leftLimit = Math.min(0, rowLeft - labelLeft);
  return { expanded: true, shift: Math.max(shift, leftLimit) };
}
